/**
 * Empezar un proyecto en blanco.
 *
 * Una pantalla y un campo. Antes esta opción abría el asistente de plantillas
 * con «en blanco» preseleccionado, así que para no elegir plantilla había que
 * entrar a un catálogo de plantillas — y ver dos pasos de tipografía y colores
 * que no aplican a un lienzo vacío.
 *
 * ── Por qué sólo el nombre ──────────────────────────────────────────────────
 *
 * Es lo único que el servidor necesita y lo único que el usuario sabe todavía.
 * La dirección se deriva de él y se hace única con un sufijo, así que no hay
 * conflictos que resolver ni nada que explicar; y la dirección pública de
 * verdad —el subdominio— se elige al publicar, que es cuando importa.
 *
 * Todo lo demás —tema, tipografía, colores, páginas— se decide en el editor,
 * con el lienzo delante. Preguntarlo aquí sería pedirlo a ciegas.
 *
 * Comparte el armazón del asistente de IA (`cwa-*`): misma barra, mismo marco y
 * misma tarjeta. Son dos formas de empezar lo mismo y deben sentirse iguales.
 */
import { DEFAULT_PAGE_THEME, applyStyleRecipe, normalizeProjectTheme, type StyleRecipe } from '@webbuilder/theme'
import { useEffect, useState } from 'react'
import { IconAlertCircle, IconLoader2, IconX } from '@tabler/icons-react'
import { createProject, isPlanLimitError } from '../../lib/api'
import { editorHref } from '../../lib/editorSession'
import { slugify } from '../../lib/preview/compose'
import { AiMascot } from './AiMascot'
import { BrandBackPill } from '../BrandBackPill'
import { useI18n } from '../../lib/i18n'
import { translate } from '../../lib/i18n'

/** Un proyecto nace con su portada, vacía. Sin ella el editor abre sin nada. */
function blankProjectData(startRecipe?: StyleRecipe | null) {
  const factory = normalizeProjectTheme(DEFAULT_PAGE_THEME)
  /* Con el estilo elegido en veltica.com, si se trae: el lienzo nace vacío pero ya con sus colores, su letra y sus formas. */
  const theme = startRecipe && Object.keys(startRecipe).length ? applyStyleRecipe(factory, startRecipe) : factory
  return {
    pages: [{
      id: crypto.randomUUID(),
      /* En el idioma de quien lo crea, y ahí se queda: el nombre de una página
         es SUYO y lo puede cambiar. No lleva clave —no es texto nuestro— así que
         no se retraduce si luego cambia de idioma, igual que si la hubiera
         llamado «Portada». */
      name: translate('pages.home'),
      slug: '/',
      isHome: true,
      elements: [],
      pageStyles: { desktop: { overflow: 'hidden', overflowY: 'visible' } },
    }],
    /* El tema de fábrica, con «Alternar fondos» encendido como todo proyecto
       nuevo: los fondos de las secciones que se agreguen se intercalan solos.
       Y con el movimiento «Suave» (Marca › Animación), como los de la IA. */
    projectTheme: { ...theme, sectionToneAuto: true, motion: { style: 'smooth' } },
  }
}

export function CreateBlankModal({ open, token, onClose, onExit, onPlanLimit, startRecipe = null, onCreated }: {
  /** El estilo elegido en veltica.com antes de entrar, si lo hay. */
  startRecipe?: StyleRecipe | null
  onCreated?: () => void
  open: boolean
  token: string
  onClose: () => void
  /**
   * Salir del todo, al panel.
   *
   * Es lo que hace la pastilla de la barra, y no es lo mismo que `onClose`: la
   * X cierra este camino y devuelve a «Comienza tu proyecto», que está justo
   * debajo. Dos salidas a distancias distintas.
   */
  onExit?: (() => void) | undefined
  /**
   * El cupo de proyectos de la membresía, lleno.
   *
   * Lo dice el servidor al crear, y no se enseña aquí: quien lo recibe cierra
   * este camino y saca el aviso en el panel, que es donde se ofrece mejorar el
   * plan. Sin esto, el tope aparecía como un renglón rojo con el JSON del error.
   */
  onPlanLimit?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [open])

  useEffect(() => {
    if (open) return
    setName('')
    setError('')
    setCreating(false)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !creating) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, creating, onClose])

  if (!open) return null

  const trimmed = name.trim()
  const ready = trimmed.length > 0 && !creating

  const create = async () => {
    if (!ready) return
    setCreating(true)
    setError('')
    try {
      /* El sufijo en base 36 hace la dirección única sin preguntar nada: dos
         proyectos llamados igual son normales, y resolver un choque de nombres
         antes de haber visto el editor es un trámite que no aporta. */
      const slug = `${slugify(trimmed)}-${Date.now().toString(36)}`
      const project = await createProject(token, trimmed, slug, blankProjectData(startRecipe))
      onCreated?.()
      window.location.href = editorHref(`?pid=${project.id}`)
    } catch (cause) {
      if (onPlanLimit && isPlanLimitError(cause, 'projects')) { setCreating(false); onPlanLimit(); return }
      setError(cause instanceof Error ? cause.message.replace(/^\d+:\s*/, '') : t('err.createProject'))
      setCreating(false)
    }
  }

  return (
    <div className="cwa-fullscreen" role="dialog" aria-modal="true" aria-label={t('createBlank.dialogLabel')}>
      <header className="cwa-topbar">
        {/* La MISMA pastilla que la pantalla de empezar: es la misma barra y no
            puede medir distinto según por qué camino se haya entrado. Aquí
            llevaba el logotipo entero a 28px y allí la pastilla a 34.

            Sale al panel; la X de la derecha vuelve a «Comienza tu proyecto».
            Son dos salidas a distancias distintas. */}
        <div className="dash-topbar-brand">
          <BrandBackPill onClick={onExit ?? onClose} disabled={creating} />
        </div>
        <button className="cwa-close dash-topbar-close" type="button" onClick={onClose} aria-label={t('createBlank.close')} disabled={creating}>
          <IconX size={16} />
        </button>
      </header>

      <div className="cwa-frame">
        <div className="cwa-prompt">
          {/* La misma mascota, en gris. Es lo que dice sin escribirlo que aquí
              no interviene la inteligencia artificial: mismo personaje, misma
              casa, otro camino. */}
          <AiMascot tone="muted" />

          <div className="cwa-prompt-copy">
            <h2>{t('createBlank.title')}</h2>
            <p>{t('createBlank.subtitle')}</p>
          </div>

          {/* La misma tarjeta y el mismo campo que el asistente de IA: son dos
              formas de empezar lo mismo y el primer paso debe sentirse igual.
              Sólo cambia que aquí es una línea en vez de un área de texto, y
              eso lo resuelve `--single`. */}
          <div className="cwa-prompt-card cwa-prompt-card--single">
            <input
              className="cwa-prompt-field cwa-prompt-field--single"
              type="text"
              value={name}
              autoFocus
              maxLength={80}
              placeholder={t('createBlank.placeholder')}
              disabled={creating}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter' && ready) void create() }}
            />
          </div>

          {error ? (
            <p className="cwa-blank-error" role="alert">
              <IconAlertCircle size={14} aria-hidden="true" /> {error}
            </p>
          ) : null}

          <div className="cwa-prompt-foot">
            <span className="cwa-prompt-hint">
              {/* Lo que viene después, dicho aquí: quien elige «en blanco» no
                  sabe si le van a pedir cinco pasos más antes del lienzo. */}
              {t('createBlank.hint')}
            </span>
            <button className="cwa-primary" type="button" disabled={!ready} onClick={() => void create()}>
              {creating ? <IconLoader2 className="cwa-spin" size={15} /> : null}
              {t(creating ? 'cwa.creating' : 'create.dialogAria')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
